import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ApplicationContext } from "../../context/GlobalState";
import { extractLocaleFromPath, localizedPath } from "../../i18n";
import "./HomeLatestNews.css";

const PAGE_SIZE = 3;
// Same brand-scope keys used by BlogPost / BlogIndex / BlogCategory.
const BRAND_SOURCE = { gso: "gaspineortho", "apex-nc": "apexosn.com", "apex-tn": "apexosntn" };

let articlesCache = null;

function formatDate(iso, locale = "en") {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleDateString(locale === "es" ? "es-US" : "en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch (e) {
    return d.toISOString().slice(0, 10);
  }
}

/**
 * Latest News home section.
 * Reads from /data/articles-scraped.json (same source as BlogPost) and
 * applies the same brand-scope source filter so links resolve cleanly.
 */
export default function HomeLatestNews() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brandKey = state?.brand?.brandKey || "gso";

  const [articles, setArticles] = useState(articlesCache || []);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    if (articlesCache) return;
    fetch("/data/articles-scraped.json")
      .then((r) => r.json())
      .then((data) => {
        articlesCache = data;
        setArticles(data);
      })
      .catch(() => { /* graceful: section just won't render */ });
  }, []);

  const sorted = useMemo(() => {
    const sourceMatch = BRAND_SOURCE[brandKey] || "";
    const scoped = sourceMatch
      ? articles.filter((a) => a && a.source && a.source.includes(sourceMatch))
      : articles;
    return [...scoped]
      .filter((a) => a && a.slug && a.title)
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [articles, brandKey]);

  const items = sorted.slice(0, visible);
  const hasMore = visible < sorted.length;

  if (items.length === 0) return null;

  const localizedTitle = (a) =>
    (locale !== "en" && a.translations && a.translations[locale] && a.translations[locale].title) || a.title;
  const localizedExcerpt = (a) =>
    (locale !== "en" && a.translations && a.translations[locale] && a.translations[locale].excerpt) || a.excerpt;

  return (
    <section className="home-latest-news section-padding" aria-labelledby="home-latest-news-heading">
      <div className="container">
        <h2 id="home-latest-news-heading" className="home-latest-news__heading">
          Latest News
        </h2>
        <p className="home-latest-news__subheading">
          Insights and updates from our orthopedic and pain-management team.
        </p>

        <ul className="home-latest-news__grid">
          {items.map((a) => {
            const cat = (a.tags && a.tags[0]) || a.category;
            return (
              <li key={a.slug} className="home-latest-news__item">
                <Link
                  to={localizedPath(`/blog/${a.slug}`, locale)}
                  className="home-latest-news__card"
                  aria-label={localizedTitle(a)}
                >
                  {cat && <span className="home-latest-news__category">{cat}</span>}
                  <h3 className="home-latest-news__title">{localizedTitle(a)}</h3>
                  {a.date && (
                    <time className="home-latest-news__date" dateTime={a.date}>
                      {formatDate(a.date, locale)}
                    </time>
                  )}
                  {localizedExcerpt(a) && (
                    <p className="home-latest-news__excerpt">{localizedExcerpt(a)}</p>
                  )}
                  <span className="home-latest-news__readmore">
                    Read more <span aria-hidden="true">→</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        {hasMore && (
          <div className="home-latest-news__more-wrap">
            <button
              type="button"
              className="home-latest-news__more"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
            >
              Load more articles
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
